/**
 * SkillDropdown - Dropdown menu for /skill command
 * Shows available skills (TFL, SCR, Define) with keyboard navigation
 */
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { AVAILABLE_SKILLS } from '@/stores/skillTaskStore';
import { Zap } from 'lucide-react';

interface SkillDropdownProps {
  show: boolean;
  selectedIndex: number;
  query: string;
  anchorRect: DOMRect | null;
  onSelect: (skillId: string, skillName: string) => void;
  onMouseEnter: (index: number) => void;
  direction?: 'up' | 'down'; // 弹出方向：up=向上（对话页面），down=向下（项目详情页）
}

const SkillDropdown = forwardRef<HTMLDivElement, SkillDropdownProps>(
  function SkillDropdown(
    {
      show,
      selectedIndex,
      query,
      anchorRect,
      onSelect,
      onMouseEnter,
      direction = 'up',
    },
    ref
  ) {
    if (!show) return null;

    // Filter skills based on query
    const filteredSkills = AVAILABLE_SKILLS.filter(skill => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        skill.name.toLowerCase().includes(q) ||
        skill.id.toLowerCase().includes(q)
      );
    });

    if (filteredSkills.length === 0) return null;

    // Position based on direction prop
    const style: React.CSSProperties = anchorRect
      ? direction === 'down'
        ? {
            top: `${anchorRect.bottom + 8}px`,
            left: `${anchorRect.left}px`,
            width: '280px',
          }
        : {
            bottom: `${window.innerHeight - anchorRect.top + 8}px`,
            left: `${anchorRect.left}px`,
            width: '280px',
          }
      : direction === 'down'
        ? {
            top: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '280px',
          }
        : {
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '280px',
          };

    return (
      <div
        ref={ref}
        className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
        style={style}
      >
        <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 flex items-center gap-1.5">
          <Zap size={12} className="text-amber-500" />
          <span>Skills</span>
        </div>
        {filteredSkills.map((skill, index) => (
          <div
            key={skill.id}
            className={cn(
              'px-3 py-2.5 cursor-pointer flex items-center gap-3 transition-colors',
              index === selectedIndex
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50'
            )}
            onClick={() => onSelect(skill.id, skill.name)}
            onMouseEnter={() => onMouseEnter(index)}
          >
            <div
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold',
                index === selectedIndex
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              )}
            >
              {skill.id.substring(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{skill.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {skill.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
);

export default SkillDropdown;
